import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Platform, Animated, StatusBar, Pressable } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { Video, ResizeMode } from 'expo-av';
import { Colors } from '@constants/colors';
import { useAuthStore } from '@store/authStore';

export const LoginScreen: React.FC = () => {
  const { signInWithGoogle, isLoading, error, clearError } = useAuthStore();
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const videoRef = useRef<Video>(null);

  useEffect(() => {
    // Fade in animation on mount
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 500,
      useNativeDriver: true,
    }).start();
  }, []);

  const handleGoogleSignIn = async () => {
    await signInWithGoogle();
  };

  // Shared UI content component
  const renderContent = () => (
    <Animated.View
      style={[styles.container, Platform.OS === 'web' && styles.webCard, { opacity: fadeAnim }]}
    >
      {/* Top Section */}
      <View style={styles.topSection}>
        {Platform.OS === 'web' ? (
          <video
            autoPlay
            loop
            muted
            playsInline
            style={{
              width: 360,
              height: 120,
              marginBottom: 12,
              objectFit: 'contain',
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
            }}
            onError={e => console.error('Video error:', e)}
            onLoadedData={() => console.log('Video loaded successfully')}
          >
            <source src={require('../../../assets/Ticker Vibes.mp4')} type="video/mp4" />
          </video>
        ) : (
          <Video
            ref={videoRef}
            source={require('../../../assets/Ticker Vibes.mp4')}
            style={styles.logoVideo}
            resizeMode={ResizeMode.CONTAIN}
            shouldPlay
            isLooping
            isMuted
          />
        )}
        <Text style={styles.welcomeText}>Welcome to TickerVibes</Text>
        <Text style={styles.subtitle}>Sign in to track stocks and get AI-powered insights</Text>
      </View>

      {/* Error Banner */}
      {error && (
        <View style={styles.errorBanner}>
          <Text style={styles.errorBannerText}>{error}</Text>
          <Pressable onPress={clearError} style={styles.errorDismiss}>
            <Ionicons name="close" size={20} color={Colors.status.error} />
          </Pressable>
        </View>
      )}

      {/* Google Sign In Button */}
      <View style={styles.authSection}>
        <Pressable
          style={[styles.googleButton, isLoading && styles.googleButtonDisabled]}
          onPress={handleGoogleSignIn}
          disabled={isLoading}
        >
          <View style={styles.googleIconContainer}>
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path
                fill="#4285F4"
                d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
              />
              <path
                fill="#34A853"
                d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"
              />
              <path
                fill="#FBBC05"
                d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"
              />
              <path
                fill="#EA4335"
                d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
              />
            </svg>
          </View>
          <Text style={styles.googleButtonText}>
            {isLoading ? 'Signing in...' : 'Continue with Google'}
          </Text>
        </Pressable>

        <Text style={styles.privacyText}>
          By continuing, you agree to our Terms of Service and Privacy Policy
        </Text>
      </View>
    </Animated.View>
  );

  // Web: Simple centered layout
  if (Platform.OS === 'web') {
    return (
      <View style={styles.webBackground}>
        <View style={styles.webCenterContainer}>{renderContent()}</View>
      </View>
    );
  }

  // Mobile: SafeAreaView
  return (
    <SafeAreaView style={styles.safeArea} edges={['top', 'bottom']}>
      <StatusBar
        barStyle="light-content"
        backgroundColor={Colors.background.primary}
        translucent={true}
      />
      <View style={styles.mobileContainer}>{renderContent()}</View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  mobileContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  // Web: Black background with centered card
  webBackground: {
    flex: 1,
    backgroundColor: '#000000',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh' as any,
  },
  webCenterContainer: {
    width: '100%',
    maxWidth: 420,
    padding: 24,
  },
  container: {
    paddingHorizontal: 24,
  },
  webCard: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: '#111827',
    borderRadius: 20,
    padding: 48,
    ...Platform.select({
      web: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 20 },
        shadowOpacity: 0.5,
        shadowRadius: 40,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.05)',
      },
    }),
  },
  topSection: {
    alignItems: 'center',
    marginBottom: Platform.OS === 'web' ? 48 : 60,
  },
  logoVideo: {
    width: 300,
    height: 100,
    marginBottom: 12,
  },
  welcomeText: {
    fontSize: Platform.OS === 'web' ? 24 : 28,
    fontWeight: '700',
    color: '#FFFFFF',
    marginTop: 0,
    letterSpacing: -0.5,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: Colors.text.secondary,
    marginTop: 12,
    textAlign: 'center',
    lineHeight: 20,
    paddingHorizontal: 20,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: `${Colors.status.error}1A`,
    borderWidth: 1,
    borderColor: Colors.status.error,
    borderRadius: 8,
    padding: 12,
    marginBottom: 24,
  },
  errorBannerText: {
    flex: 1,
    color: Colors.status.error,
    fontSize: 13,
  },
  errorDismiss: {
    padding: 4,
  },
  authSection: {
    alignItems: 'center',
  },
  googleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 24,
    width: '100%',
    gap: 12,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      },
    }),
  },
  googleButtonDisabled: {
    opacity: 0.6,
    ...Platform.select({
      web: {
        cursor: 'not-allowed',
      },
    }),
  },
  googleIconContainer: {
    width: 18,
    height: 18,
  },
  googleButtonText: {
    color: '#1F2937',
    fontSize: 16,
    fontWeight: '600',
  },
  privacyText: {
    marginTop: 24,
    fontSize: 12,
    color: Colors.text.tertiary,
    textAlign: 'center',
    lineHeight: 18,
    paddingHorizontal: 20,
  },
});
